import React, { useEffect, useContext } from "react";
import styled from "styled-components";
import { ThemeProvider } from "styled-components";
import { withStoreContext } from "gatsby-plugin-shopify-buy";
import Cookies from "universal-cookie";

import SEO from "./seo";
import Botbar from "./botbar/";
import BuyPopup from "./common/buyPopup";
import InstallPopup from "./common/installPopup";
import IntlPopup from "./common/intlPopup";
import SizePopup from "./common/sizePopup";
import "./layout.css";
import Topbar from "../components/topbar/";
import { GlobalDispatchContext, GlobalStateContext } from "./context";
import { fetchGeolocation } from "../utils";

const theme = {
  responsive: {
    mobileSmall: "@media (min-width: 420px)",
    mobile: "@media (min-width: 768px)",
    desktop: "@media (min-width: 1024px)",
    fullhd: "@media (min-width: 1216px)",
  },
  font: {
    sans: "Founders, sans",
    serif: "Reckless, serif",
  },
  color: {
    red: "#E63946",
    redLight: "#ba2330",
    blue: "#95AFBA",
    black: "#111111",
    darkGrey: "#151515",
    dimmerGrey: "#545454",
    dimGrey: "#313131",
    grey: "#8c8c8c",
    lightGrey: "#E6E6E6",
    smoke: "#F4F4F4",
    white: "#FFFFFF",
    indigo: "#52183e",
    olive: "#636353",
    violet: "#653875",
    kcYellow: "#FAD900",
    brown: "#6e1c23",
    coffee: "#57332d",
    lightGreen: "#00c47b",
    green: "#004a44",
    purple: "#592336",
    cream: "#f0e3cc",
    darkCream: "#272724",
  },
  shadow: "box-shadow: rgba(0, 0, 0, 0.15) 0px 0.0625rem 0.375rem;",
  noSelect: `
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Old versions of Firefox */
            -ms-user-select: none; /* Internet Explorer/Edge */
                user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome, Edge, Opera and Firefox */
    `,
};

const StyledWrapper = styled.div`
  position: relative;
  font-family: Founders, sans;
  ${(props) =>
    props.isLockable &&
    `
        height: 100%;
        overflow: auto;
        `}
`;

const StyledContent = styled.div`
  font-family: ${(props) => props.theme.font.sans};
`;

const Layout = ({
  children,
  title = "King Children",
  location,
  isLockable = true,
  isHideBotbar,
  handlerScroll,
  storeContext,
  topbarColor,
  isAppView = false,
  isShopPage = false,
  ...props
}) => {
  const state = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const sku = state.curSKU;
  const cookies = new Cookies();

  useEffect(() => {
    if (isAppView) {
      window.$crisp.push(["do", "chat:hide"]);
    }
  }, [isAppView]);

  const createNewShopifyCheckout = (prevCheckout) => {
    let input = {
      presentmentCurrencyCode: state.currency,
    };
    if (state.email) {
      input["email"] = state.email;
    }
    if (prevCheckout) {
      input["lineItems"] = prevCheckout.lineItems.map((i) => {
        return { variantId: i.variant.id, quantity: i.quantity };
      });
    }
    storeContext.client.checkout
      .create(input)
      .then((checkout) => {
        cookies.set("checkoutID", checkout.id, { path: "/" });
        dispatch({
          type: "SET_SHOPIFY_CHECKOUT",
          shopifyCheckout: checkout,
        });
      })
      .catch((error) => {
        createNewShopifyCheckout();
      });
  };

  useEffect(() => {
    if (state.email === undefined) {
      const email = cookies.get("email");
      if (email != undefined) {
        dispatch({
          type: "SET_EMAIL",
          email: email,
        });
      }
    }

    if (state.shopifyCheckout === undefined) {
      const checkoutID = cookies.get("checkoutID");
      if (checkoutID === undefined) {
        // create new checkout
        createNewShopifyCheckout();
      } else {
        storeContext.client.checkout
          .fetch(checkoutID)
          .then((checkout) => {
            if (checkout.completedAt === null) {
              dispatch({
                type: "SET_SHOPIFY_CHECKOUT",
                shopifyCheckout: checkout,
              });
            } else {
              createNewShopifyCheckout();
            }
          })
          .catch((error) => {
            createNewShopifyCheckout();
          });
      }
    }
  }, []);

  useEffect(() => {
    if (state.currency == null || state.currencyCountry == null) {
      fetchGeolocation()
        .then((res) => {
          dispatch({
            type: "SET_CURRENCY",
            currency: res.currency,
            country: res.country,
          });
        })
        .catch(
          dispatch({
            type: "SET_CURRENCY",
            currency: "USD",
            country: "United States",
          })
        );
    } else {
      if (state.shopifyCheckout !== undefined) {
        createNewShopifyCheckout(state.shopifyCheckout);
      }
    }
  }, [state.currency, state.currencyCountry]);

  const handleScroll = (e) => {
    if (handlerScroll) {
      handlerScroll(e);
    }
  };

  return (
    <StyledWrapper
      isLockable={isLockable || state.isBuyPopupShown}
      onScroll={(e) => handleScroll(e)}
    >
      <SEO title={title} />
      <ThemeProvider theme={theme}>
        {!isAppView && (
          <>
            <Topbar
              color={topbarColor}
              location={location}
              isShopPage={isShopPage}
            />
            <BuyPopup />
            <InstallPopup />
            <IntlPopup />
            <SizePopup />
          </>
        )}
        <StyledContent>{children}</StyledContent>
        {!isAppView && !isHideBotbar && <Botbar />}
      </ThemeProvider>
    </StyledWrapper>
  );
};

export default withStoreContext(Layout);
